import { approxYear, sortKey } from '../../domain/dates';
import { isAdmin, isEditor } from '../../domain/permissions';
import { branchOf, buildGraph } from '../../domain/relationships';
import { mayDecideStorySuggestions, mayEditStory, mayWithdrawSuggestion } from '../../domain/storyEditing';
import type { CommandResult, FamilyEvent, FuzzyDate, Story, StoryContribution, StoryPerson, StoryPersonRole, StoryQuestion, StorySuggestion, StorySuggestionChange, Transcript, UUID } from '../../domain/types';
import { nowISO } from '../../lib/time';
import { uuid } from '../../lib/uuid';
import type { PublishStoryCommand, StoryDetail, StoryFilter, StoryRepository } from '../types';
import { actorOf, copies, denied, guarded, invalid, notFound, okResult, personIdsManagedBy } from './actor';
import { MockDb } from './MockDb';
import { MockMediaRepository } from './MockMediaRepository';

export class MockStoryRepository implements StoryRepository {
  constructor(private db: MockDb, private media: MockMediaRepository) {}

  private detail(storyId: UUID): StoryDetail | undefined {
    const story = this.db.t.stories.find((s) => s.id === storyId);
    if (!story) return undefined;
    const contributions = this.db.t.story_contributions.filter((c) => c.storyId === storyId && c.status !== 'removed').sort((a, b) => a.order - b.order);
    const eventIds = this.db.t.story_events.filter((se) => se.storyId === storyId).map((se) => se.eventId);
    return {
      story,
      contributions,
      people: this.db.t.story_people.filter((sp) => sp.storyId === storyId),
      events: this.db.t.events.filter((e) => eventIds.includes(e.id)),
      transcripts: this.db.t.transcripts.filter((t) => contributions.some((c) => c.id === t.contributionId)),
      media: this.db.t.media_assets.filter((m) => contributions.some((c) => c.mediaAssetId === m.id)),
      // Open ones first, oldest first within each group: the teller works down a queue.
      suggestions: this.db.t.story_suggestions
        .filter((s) => s.storyId === storyId && s.status !== 'withdrawn')
        .sort((a, b) => (a.status === 'open' ? 0 : 1) - (b.status === 'open' ? 0 : 1) || a.createdAt.localeCompare(b.createdAt)),
    };
  }

  async list(filter: StoryFilter = {}): Promise<Story[]> {
    await this.db.read();
    let rows = this.db.t.stories.filter((s) => s.status === 'published' || (filter.includeDrafts && s.status === 'draft'));
    if (filter.personId) {
      const ids = new Set(this.db.t.story_people.filter((sp) => sp.personId === filter.personId).map((sp) => sp.storyId));
      for (const c of this.db.t.story_contributions) if (c.speakerPersonId === filter.personId) ids.add(c.storyId);
      rows = rows.filter((s) => ids.has(s.id) || s.primaryStorytellerPersonId === filter.personId);
    }
    if (filter.storytellerId) {
      const ids = new Set(this.db.t.story_contributions.filter((c) => c.speakerPersonId === filter.storytellerId && c.status === 'published').map((c) => c.storyId));
      rows = rows.filter((s) => s.primaryStorytellerPersonId === filter.storytellerId || ids.has(s.id));
    }
    if (filter.branchRootId) {
      const g = buildGraph(this.db.t.persons.map((p) => p.id), this.db.t.relationships);
      const branch = branchOf(g, filter.branchRootId);
      rows = rows.filter((s) => branch.has(s.primaryStorytellerPersonId) || this.db.t.story_people.some((sp) => sp.storyId === s.id && branch.has(sp.personId)));
    }
    if (filter.fromYear !== undefined) rows = rows.filter((s) => { const y = approxYear(s.date); return y === null || y >= filter.fromYear!; });
    if (filter.toYear !== undefined) rows = rows.filter((s) => { const y = approxYear(s.date); return y === null || y <= filter.toYear!; });
    if (filter.locationId) rows = rows.filter((s) => s.locationId === filter.locationId);
    if (filter.kind && filter.kind !== 'all') {
      rows = rows.filter((s) => this.db.t.story_contributions.some((c) => c.storyId === s.id && c.kind === filter.kind));
    }
    if (filter.query?.trim()) {
      const q = filter.query.trim().toLowerCase();
      rows = rows.filter((s) => {
        if (s.title.toLowerCase().includes(q) || s.summary?.toLowerCase().includes(q)) return true;
        const cids = this.db.t.story_contributions.filter((c) => c.storyId === s.id).map((c) => c.id);
        return this.db.t.transcripts.some((t) => cids.includes(t.contributionId) && t.status === 'ready' && t.reviewStatus !== 'unreviewed' && t.text.toLowerCase().includes(q))
          || this.db.t.story_contributions.some((c) => c.storyId === s.id && c.text?.toLowerCase().includes(q));
      });
    }
    return copies(rows.sort((a, b) => sortKey(a.date) - sortKey(b.date)));
  }

  async get(id: UUID): Promise<StoryDetail | undefined> {
    await this.db.read();
    return this.detail(id);
  }

  async listContributions(): Promise<StoryContribution[]> {
    await this.db.read();
    return copies(this.db.t.story_contributions.filter((c) => c.status !== 'removed'));
  }

  async listStoryPeople(): Promise<StoryPerson[]> {
    await this.db.read();
    return copies(this.db.t.story_people);
  }

  async listEvents(): Promise<FamilyEvent[]> {
    await this.db.read();
    return [...this.db.t.events];
  }

  /**
   * Every published story, to everybody. A story is the family's, not the
   * recorder's — see the note on `Story` in domain/types.ts. Only two things
   * are held back: an archived story (an administrator's business) and someone
   * else's unpublished draft.
   */
  async visibleTo(accountId: UUID): Promise<Story[]> {
    await this.db.read();
    const admin = this.db.membership(accountId)?.role === 'admin';
    return copies(
      this.db.t.stories.filter((s) => {
        if (s.status === 'archived') return admin;
        if (s.status === 'draft') return s.creatorAccountId === accountId;
        return true;
      }),
    );
  }

  async publishFromDraft(cmd: PublishStoryCommand): Promise<CommandResult<StoryDetail>> {
    return guarded(this.db, async () => {
      const db = this.db;
      const actor = actorOf(db);
      const idem = db.idem(cmd.idempotencyKey);
      if (idem) {
        const d = this.detail(idem.entityId);
        return d ? okResult(d, { noop: true }) : notFound();
      }
      const draft = db.t.recording_drafts.find((d) => d.id === cmd.draftId);
      if (!draft) return notFound();
      if (!draft.mediaAssetId && !draft.textFallback?.trim()) return invalid('record.error.empty');
      if (!personIdsManagedBy(db, actor.session.accountId).includes(draft.speakerPersonId) && !isEditor(actor.session.role)) return denied('record.error.notAllowedFor');
      const now = nowISO();
      const storyId = uuid();
      const story: Story = {
        id: storyId, familyId: actor.session.familyId, title: draft.titlePending || !draft.title.trim() ? '' : draft.title.trim(), titlePending: draft.titlePending || !draft.title.trim(),
        date: draft.date, locationId: draft.locationId, status: 'published', creatorAccountId: actor.session.accountId,
        primaryStorytellerPersonId: draft.speakerPersonId, publishedAt: now, locked: false, idempotencyKey: cmd.idempotencyKey, version: 1, createdAt: now, updatedAt: now, updatedBy: actor.session.accountId,
      };
      const contribution: StoryContribution = {
        id: uuid(), storyId, speakerPersonId: draft.speakerPersonId, uploaderAccountId: actor.session.accountId, order: 0, kind: draft.mediaAssetId ? 'audio' : 'text',
        mediaAssetId: draft.mediaAssetId, text: draft.mediaAssetId ? undefined : draft.textFallback?.trim(), status: 'published', idempotencyKey: `${cmd.idempotencyKey}:c0`,
        version: 1, createdAt: now, updatedAt: now, updatedBy: actor.session.accountId,
      };
      // Atomic: story + first contribution + people links committed together (section 16.5)
      db.t.stories.push(story);
      db.t.story_contributions.push(contribution);
      const peopleIds = new Set([draft.speakerPersonId, ...draft.peopleIds]);
      for (const pid of peopleIds) {
        db.t.story_people.push({ id: uuid(), storyId, personId: pid, role: pid === draft.speakerPersonId ? 'storyteller' : 'participant' });
      }
      db.t.recording_drafts = db.t.recording_drafts.filter((d) => d.id !== draft.id);
      db.rememberIdem(cmd.idempotencyKey, 'story', storyId);
      db.audit({
        actorAccountId: actor.session.accountId, onBehalfOfPersonId: draft.speakerPersonId !== actor.session.personId ? draft.speakerPersonId : undefined,
        entityType: 'story', entityId: storyId, action: 'publish', description: `Published “${story.title || '(untitled)'}”`, entityVersion: 1,
      });
      if (contribution.kind === 'text') {
        db.t.transcripts.push({ id: uuid(), contributionId: contribution.id, language: 'unknown', text: contribution.text ?? '', isTranslation: false, source: 'human', status: 'ready', reviewStatus: 'approved', version: 1, updatedAt: now });
      }
      const who = db.accountName(actor.session.accountId);
      db.notifyFamily(actor.session.accountId, { kind: 'story', actorAccountId: actor.session.accountId, title: `${who} shared a story: “${story.title || 'untitled'}”`, titleHe: `${who} שיתף/ה סיפור: "${story.title || 'ללא כותרת'}"`, link: `/stories/${storyId}` });
      // A recording made in answer to a question closes it and tells the asker (same transaction as the story).
      const question = draft.questionId ? db.t.story_questions.find((q) => q.id === draft.questionId && q.status === 'open' && q.toPersonId === draft.speakerPersonId) : undefined;
      if (question) {
        question.status = 'answered';
        question.answerStoryId = storyId;
        question.answeredAt = now;
        const speaker = db.personWithNames(draft.speakerPersonId);
        const speakerName = speaker?.names.find((n) => n.language === 'en')?.given ?? speaker?.names[0]?.given ?? who;
        const speakerNameHe = speaker?.names.find((n) => n.language === 'he')?.given ?? speakerName;
        if (question.askedByAccountId !== actor.session.accountId) {
          db.notify({ accountId: question.askedByAccountId, kind: 'question', actorAccountId: actor.session.accountId, title: `${speakerName} answered your question`, titleHe: `${speakerNameHe} ענה/תה על השאלה שלך`, body: question.text, bodyHe: question.text, link: `/stories/${storyId}` });
        }
      }
      await db.commit(['stories', 'story_contributions', 'story_people', 'recording_drafts', 'idempotency', 'audit_events', 'transcripts', 'notifications', 'story_questions'], [storyId, draft.speakerPersonId]);
      if (draft.mediaAssetId) void this.media.startUpload(draft.mediaAssetId, `${cmd.idempotencyKey}:upload`);
      return okResult(this.detail(storyId)!);
    });
  }

  private async patchStory(storyId: UUID, expectedVersion: number, patch: Partial<Story>, idempotencyKey: string, description: string, field: string, reason?: string): Promise<CommandResult<Story>> {
    return guarded(this.db, async () => {
      const db = this.db;
      const actor = actorOf(db);
      const story = db.t.stories.find((s) => s.id === storyId);
      if (!story) return notFound();
      if (db.idem(idempotencyKey)) return okResult(story, { noop: true });
      // A story belongs to whoever recorded it (domain/storyEditing.ts). Everyone
      // else suggests; an administrator changes it directly.
      if (!mayEditStory(actor, story)) return denied('story.error.suggestInstead');
      if (story.locked && !isAdmin(actor.session.role)) return { ok: false, kind: 'locked', messageKey: 'story.locked' };
      if (story.version !== expectedVersion) {
        const last = [...db.t.audit_events].reverse().find((a) => a.entityId === storyId && a.action === 'update');
        return { ok: false, kind: 'conflict', conflicts: [{ field, currentValue: (story as unknown as Record<string, unknown>)[field], yourValue: (patch as Record<string, unknown>)[field], baseValue: null, changedByAccountId: story.updatedBy, changedAt: last?.timestamp ?? story.updatedAt }], current: story, currentVersion: story.version };
      }
      const before = { ...story };
      Object.assign(story, patch);
      story.version += 1;
      story.updatedAt = nowISO();
      story.updatedBy = actor.session.accountId;
      db.audit({ actorAccountId: actor.session.accountId, entityType: 'story', entityId: storyId, action: 'update', before: { [field]: (before as unknown as Record<string, unknown>)[field] }, after: { [field]: (story as unknown as Record<string, unknown>)[field] }, description, reason: reason?.trim() || undefined, entityVersion: story.version });
      db.rememberIdem(idempotencyKey, 'story', storyId);
      await db.commit(['stories', 'audit_events', 'idempotency'], [storyId]);
      return okResult(story);
    });
  }

  updateStoryDate(storyId: UUID, expectedVersion: number, date: FuzzyDate, idempotencyKey: string): Promise<CommandResult<Story>> {
    return this.patchStory(storyId, expectedVersion, { date }, idempotencyKey, 'Story date changed', 'date');
  }

  updateStoryTitle(storyId: UUID, expectedVersion: number, title: string, idempotencyKey: string): Promise<CommandResult<Story>> {
    return this.patchStory(storyId, expectedVersion, { title: title.trim(), titlePending: !title.trim() }, idempotencyKey, 'Story title changed', 'title');
  }


  async setStoryPeople(storyId: UUID, people: Array<{ personId: UUID; role: StoryPersonRole }>): Promise<CommandResult<StoryPerson[]>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const story = this.db.t.stories.find((s) => s.id === storyId);
      if (!story) return notFound();
      // Who is in a story is part of the story, so it follows the same rule as
      // its title and its date: the teller and administrators, nobody else.
      if (!mayEditStory(actor, story)) return denied('story.error.suggestInstead');
      if (story.locked && !isAdmin(actor.session.role)) return { ok: false, kind: 'locked', messageKey: 'story.locked' };
      const keep = this.db.t.story_people.filter((sp) => sp.storyId !== storyId);
      const rows: StoryPerson[] = people.map((p) => ({ id: uuid(), storyId, personId: p.personId, role: p.role }));
      this.db.t.story_people = [...keep, ...rows];
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'story', entityId: storyId, action: 'update', description: 'People in story updated' });
      await this.db.commit(['story_people', 'audit_events'], [storyId]);
      return okResult(rows);
    });
  }

  async removeContribution(contributionId: UUID, expectedVersion: number, idempotencyKey: string, reason?: string): Promise<CommandResult<{ removed: true }>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const c = this.db.t.story_contributions.find((x) => x.id === contributionId);
      if (!c) return okResult({ removed: true as const }, { noop: true });
      const own = c.uploaderAccountId === actor.session.accountId || personIdsManagedBy(this.db, actor.session.accountId).includes(c.speakerPersonId);
      if (!own && !isAdmin(actor.session.role)) return denied();
      if (c.version !== expectedVersion) return { ok: false, kind: 'conflict', conflicts: [], current: { removed: true as const }, currentVersion: c.version };
      c.status = 'removed'; // soft delete; other contributions untouched (section 12.5)
      c.version += 1;
      c.updatedAt = nowISO();
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'contribution', entityId: c.id, action: 'archive', description: 'Contribution removed (recoverable)', reason: reason?.trim() || undefined, entityVersion: c.version });
      this.db.rememberIdem(idempotencyKey, 'contribution', c.id);
      await this.db.commit(['story_contributions', 'audit_events', 'idempotency'], [c.storyId]);
      return okResult({ removed: true as const });
    });
  }

  async archiveStory(storyId: UUID, expectedVersion: number, reason?: string): Promise<CommandResult<Story>> {
    return this.patchStory(storyId, expectedVersion, { status: 'archived' }, `archive:${storyId}:${expectedVersion}`, 'Story archived (recoverable)', 'status', reason);
  }

  async restoreStory(storyId: UUID): Promise<CommandResult<Story>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      if (!isAdmin(actor.session.role)) return denied();
      const story = this.db.t.stories.find((s) => s.id === storyId);
      if (!story) return notFound();
      story.status = 'published';
      story.version += 1;
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'story', entityId: storyId, action: 'restore', description: 'Story restored', entityVersion: story.version });
      await this.db.commit(['stories', 'audit_events'], [storyId]);
      return okResult(story);
    });
  }

  async lockStory(storyId: UUID, locked: boolean): Promise<CommandResult<Story>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      if (!isAdmin(actor.session.role)) return denied();
      const story = this.db.t.stories.find((s) => s.id === storyId);
      if (!story) return notFound();
      story.locked = locked;
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'story', entityId: storyId, action: locked ? 'lock' : 'unlock', description: locked ? 'Story locked' : 'Story unlocked' });
      await this.db.commit(['stories', 'audit_events'], [storyId]);
      return okResult(story);
    });
  }

  async correctTranscript(transcriptId: UUID, text: string): Promise<CommandResult<Transcript>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const tr = this.db.t.transcripts.find((t) => t.id === transcriptId);
      if (!tr) return notFound();
      tr.text = text;
      tr.reviewStatus = 'corrected';
      tr.source = 'human';
      tr.status = 'ready';
      tr.version += 1;
      tr.updatedAt = nowISO();
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'contribution', entityId: tr.contributionId, action: 'update', description: 'Transcript corrected by a person' });
      await this.db.commit(['transcripts', 'audit_events']);
      return okResult(tr);
    });
  }

  async react(storyId: UUID, emoji: string | null): Promise<CommandResult<Story>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const story = this.db.t.stories.find((s) => s.id === storyId);
      if (!story) return notFound();
      if (story.status !== 'published' || !(await this.visibleTo(actor.session.accountId)).some((s) => s.id === storyId)) return denied();
      const mine = actor.session.accountId;
      const rest = (story.reactions ?? []).filter((r) => r.accountId !== mine);
      story.reactions = emoji ? [...rest, { accountId: mine, emoji, createdAt: nowISO() }] : rest;
      await this.db.commit(['stories'], [storyId]);
      return okResult(story);
    });
  }

  // ---- suggestions (domain/storyEditing.ts) ------------------------------

  async listStorySuggestions(storyId?: UUID): Promise<StorySuggestion[]> {
    await this.db.read();
    return this.db.t.story_suggestions.filter((s) => (storyId ? s.storyId === storyId : true) && s.status !== 'withdrawn');
  }

  async suggestStoryChange(storyId: UUID, change: StorySuggestionChange, note?: string): Promise<CommandResult<StorySuggestion>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const story = this.db.t.stories.find((s) => s.id === storyId);
      if (!story) return notFound();
      if (story.status !== 'published') return denied();
      // The teller and administrators do not suggest — they change it.
      if (mayEditStory(actor, story)) return invalid('story.error.editDirectly');

      // One open suggestion per person per kind: saying it again replaces what
      // you said before, so the teller never has two of your titles to choose.
      const mine = this.db.t.story_suggestions.find(
        (s) => s.storyId === storyId && s.status === 'open' && s.change.kind === change.kind && s.suggestedByAccountId === actor.session.accountId,
      );
      const row: StorySuggestion = mine ?? {
        id: uuid(),
        familyId: story.familyId,
        storyId,
        change,
        suggestedByAccountId: actor.session.accountId,
        status: 'open',
        createdAt: nowISO(),
      };
      row.change = change;
      row.note = note?.trim() || undefined;
      if (!mine) this.db.t.story_suggestions.push(row);

      this.db.audit({
        actorAccountId: actor.session.accountId, entityType: 'story', entityId: storyId, action: 'update',
        description: `Suggested a change to “${story.title}” (${change.kind})`, reason: row.note,
      });
      await this.db.commit(['story_suggestions', 'audit_events'], [storyId]);
      return okResult(row);
    });
  }

  async acceptStorySuggestion(id: UUID): Promise<CommandResult<Story>> {
    const row = this.db.t.story_suggestions.find((s) => s.id === id);
    if (!row) return notFound();
    const story = this.db.t.stories.find((s) => s.id === row.storyId);
    if (!story) return notFound();
    const actor = actorOf(this.db);
    if (!mayDecideStorySuggestions(actor, story)) return denied();
    if (row.status !== 'open') return okResult(story, { noop: true });

    // Apply it through the ordinary edit path, so the change is versioned,
    // audited and conflict-checked exactly as if the teller had typed it.
    const applied =
      row.change.kind === 'title'
        ? await this.updateStoryTitle(story.id, story.version, row.change.title, uuid())
        : row.change.kind === 'date'
          ? await this.updateStoryDate(story.id, story.version, row.change.date, uuid())
          : await this.setStoryPeople(story.id, row.change.personIds.map((personId) => ({ personId, role: 'participant' as const }))).then(
              (r) => (r.ok ? okResult(story) : (r as CommandResult<Story>)),
            );
    if (!applied.ok) return applied;

    return guarded(this.db, async () => {
      row.status = 'accepted';
      row.decidedAt = nowISO();
      row.decidedByAccountId = actor.session.accountId;
      this.db.audit({
        actorAccountId: actor.session.accountId, entityType: 'story', entityId: story.id, action: 'approve',
        description: `Accepted a suggested change to “${story.title}” (${row.change.kind})`,
      });
      await this.db.commit(['story_suggestions', 'audit_events'], [story.id]);
      return okResult(this.db.t.stories.find((s) => s.id === story.id)!);
    });
  }

  async declineStorySuggestion(id: UUID): Promise<CommandResult<StorySuggestion>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const row = this.db.t.story_suggestions.find((s) => s.id === id);
      if (!row) return notFound();
      const story = this.db.t.stories.find((s) => s.id === row.storyId);
      if (!story) return notFound();
      if (!mayDecideStorySuggestions(actor, story)) return denied();
      if (row.status !== 'open') return okResult(row, { noop: true });
      row.status = 'declined';
      row.decidedAt = nowISO();
      row.decidedByAccountId = actor.session.accountId;
      this.db.audit({
        actorAccountId: actor.session.accountId, entityType: 'story', entityId: story.id, action: 'reject',
        description: `Declined a suggested change to “${story.title}” (${row.change.kind})`,
      });
      await this.db.commit(['story_suggestions', 'audit_events'], [story.id]);
      return okResult(row);
    });
  }

  async withdrawStorySuggestion(id: UUID): Promise<CommandResult<StorySuggestion>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const row = this.db.t.story_suggestions.find((s) => s.id === id);
      if (!row) return notFound();
      if (!mayWithdrawSuggestion(actor, row.suggestedByAccountId)) return denied();
      if (row.status !== 'open') return okResult(row, { noop: true });
      row.status = 'withdrawn';
      row.decidedAt = nowISO();
      await this.db.commit(['story_suggestions'], [row.storyId]);
      return okResult(row);
    });
  }

  async listQuestions(): Promise<StoryQuestion[]> {
    await this.db.read();
    return [...this.db.t.story_questions];
  }

  async askQuestion(toPersonId: UUID, text: string, idempotencyKey: string): Promise<CommandResult<StoryQuestion>> {
    return guarded(this.db, async () => {
      const db = this.db;
      const actor = actorOf(db);
      const idem = db.idem(idempotencyKey);
      if (idem) {
        const prior = db.t.story_questions.find((q) => q.id === idem.entityId);
        if (prior) return okResult(prior, { noop: true });
      }
      const clean = text.trim();
      if (!clean) return invalid('ask.error.empty');
      const person = db.t.persons.find((p) => p.id === toPersonId && !p.archived && !p.mergedIntoId);
      if (!person) return notFound();
      if (person.status === 'deceased') return invalid('ask.error.deceased');
      const now = nowISO();
      const q: StoryQuestion = {
        id: uuid(), familyId: actor.session.familyId, toPersonId, askedByAccountId: actor.session.accountId, askedByPersonId: actor.session.personId ?? undefined,
        text: clean.slice(0, 240), status: 'open', createdAt: now,
      };
      db.t.story_questions.push(q);
      db.rememberIdem(idempotencyKey, 'question', q.id);
      db.audit({ actorAccountId: actor.session.accountId, entityType: 'story', entityId: q.id, action: 'create', description: `Asked a question: ${q.text}` });
      // The people who can answer — the person themself and anyone who records for them — hear about it.
      const who = db.accountName(actor.session.accountId);
      for (const accountId of db.accountsForPerson(toPersonId)) {
        if (accountId === actor.session.accountId) continue;
        db.notify({ accountId, kind: 'question', actorAccountId: actor.session.accountId, title: `${who} asked a question`, titleHe: `${who} שאל/ה שאלה`, body: q.text, bodyHe: q.text, link: `/record?question=${q.id}` });
      }
      await db.commit(['story_questions', 'idempotency', 'audit_events', 'notifications'], [toPersonId]);
      return okResult(q);
    });
  }

  async dismissQuestion(questionId: UUID): Promise<CommandResult<StoryQuestion>> {
    return guarded(this.db, async () => {
      const db = this.db;
      const actor = actorOf(db);
      const q = db.t.story_questions.find((x) => x.id === questionId);
      if (!q) return notFound();
      const mayDismiss = isAdmin(actor.session.role) || q.askedByAccountId === actor.session.accountId || personIdsManagedBy(db, actor.session.accountId).includes(q.toPersonId);
      if (!mayDismiss) return denied();
      if (q.status !== 'open') return okResult(q, { noop: true });
      q.status = 'dismissed';
      db.audit({ actorAccountId: actor.session.accountId, entityType: 'story', entityId: q.id, action: 'archive', description: 'Question set aside' });
      await db.commit(['story_questions', 'audit_events'], [q.toPersonId]);
      return okResult(q);
    });
  }
}
